import { activeRoleIdOf } from "./token-encoding.utils";

/**
 * The role and channel a write lands in, as one comparable key: the role on
 * screen, the role the access token acts under, and the channel sent as
 * `x-channel`. The BFF takes a bound role's channel from the token, which
 * moves before the role on screen after a switch in another tab, and an App
 * Owner's from `x-channel`. A write planned under one key must not go out
 * under another. Keyed on the token's role, not the token itself: a refresh
 * mints a new token for the same role.
 */
export function writeScopeKey(
  screenRoleId: number | null | undefined,
  accessToken: string | null,
  channel: string,
): string {
  return JSON.stringify([screenRoleId, activeRoleIdOf(accessToken), channel]);
}

/** The same scope as a value, read as a request is built or a page loads. */
export interface WriteScope {
  /** `RoleService.selectionKey()`: the role and channel on screen, null
   *  before any role is selected. */
  readonly selection: string | null;
  readonly screenRoleId: number | null;
  /** The role the access token acts under, null for a token without one. */
  readonly tokenRoleId: number | null;
  /** The channel the BFF resolves the request to: a bound role's own, else
   *  the one sent as `x-channel`. */
  readonly channel: string;
}

/** The token acts as the role on screen. A screen with no role yet, or a
 *  token with no role claim, makes no counter-claim. */
export const isSettledWriteScope = (scope: WriteScope): boolean =>
  scope.tokenRoleId === null ||
  scope.screenRoleId === null ||
  scope.tokenRoleId === scope.screenRoleId;

/**
 * Whether a write built under `current` may go out for a page loaded under
 * `loaded`: the scope is settled, and neither the selection on screen, the
 * token's role nor the channel moved since the load.
 */
export function writeScopeHolds(
  current: WriteScope,
  loaded: WriteScope,
): boolean {
  return (
    isSettledWriteScope(current) &&
    current.selection === loaded.selection &&
    current.tokenRoleId === loaded.tokenRoleId &&
    current.channel === loaded.channel
  );
}
